/**
 * AI Truth Dashboard Tab — Sprint C10.
 *
 * Full measurement surface for a domain's AI Truth performance:
 *   - Composite AI Accuracy Score + band badge + grace-period note
 *   - Component breakdown (Coverage, Grounding, Serving, Reach, Freshness)
 *   - 30-day score trend sparkline
 *   - Pending TruthSynq re-drafts (deleted-source flagged urgent)
 *   - Visit Length per engine (standalone, 10-session guard)
 *   - Question Anticipation count (standalone)
 *   - Quick links to wizard + answer management
 *
 * Design system: FixMySEO — brand-green/gold/coral, Lora/Poppins, card pattern.
 * Gated: entitlement checked server-side; this component only mounts when entitled.
 */

import { useState, useEffect } from 'react';
import { api } from '../../api';

interface Props {
  domainId: string;
  domain:   string;
  tier:     string;
  onSetupWizard?: () => void;
}

// ── Band configuration ────────────────────────────────────────────────────────

const BAND_CONFIG: Record<string, { label: string; color: string; bg: string; border: string }> = {
  excellent: { label: 'Excellent', color: 'text-brand-green-dark', bg: 'bg-emerald-50',  border: 'border-emerald-300' },
  strong:    { label: 'Strong',    color: 'text-brand-green',      bg: 'bg-emerald-50',  border: 'border-emerald-200' },
  good:      { label: 'Good',      color: 'text-brand-green',      bg: 'bg-green-50',    border: 'border-green-200' },
  building:  { label: 'Building',  color: 'text-amber-700',        bg: 'bg-amber-50',    border: 'border-amber-200' },
  poor:      { label: 'Poor',      color: 'text-red-600',          bg: 'bg-red-50',      border: 'border-red-200' },
};

const COMPONENT_LABELS: Record<string, { label: string; desc: string }> = {
  coverage:  { label: 'Coverage',   desc: 'Approved answers vs. anticipated questions' },
  grounding: { label: 'Grounding',  desc: 'Site-sourced vs. owner-claimed answers' },
  serving:   { label: 'Serving',    desc: 'Query match rate + citation rate' },
  reach:     { label: 'Reach',      desc: 'Active AI Truth endpoint' },
  freshness: { label: 'Freshness',  desc: 'Inverse of pending TruthSynq re-drafts' },
};

const ENGINE_LABELS: Record<string, string> = {
  chatgpt:             'ChatGPT',
  perplexity:          'Perplexity',
  gemini:              'Gemini',
  claude:              'Claude',
  mistral:             'Mistral',
  google_ai_overviews: 'Google AI Overviews',
  unknown:             'Other / Unknown',
};

// ── Small primitives ──────────────────────────────────────────────────────────

function ScoreRing({ score, band }: { score: number; band: string }) {
  const cfg    = BAND_CONFIG[band] ?? BAND_CONFIG['building']!;
  const radius = 52;
  const circ   = 2 * Math.PI * radius;
  const fill   = circ * (score / 100);

  return (
    <div className="relative w-36 h-36 flex items-center justify-center">
      <svg width="144" height="144" viewBox="0 0 144 144" className="-rotate-90">
        <circle cx="72" cy="72" r={radius} fill="none" stroke="#E5E7EB" strokeWidth="10" />
        <circle
          cx="72" cy="72" r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="10"
          strokeDasharray={`${fill} ${circ}`}
          strokeLinecap="round"
          className={cfg.color}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold font-serif ${cfg.color}`}>{Math.round(score)}</span>
        <span className={`text-xs font-medium ${cfg.color} mt-0.5`}>{cfg.label}</span>
      </div>
    </div>
  );
}

function ComponentBar({ name, score, weight, gracePeriodExcluded }: {
  name: string; score: number | null; weight: number; gracePeriodExcluded?: boolean;
}) {
  const info = COMPONENT_LABELS[name] ?? { label: name, desc: '' };
  const pct  = score ?? 0;
  const barColor = pct >= 80 ? 'bg-brand-green' : pct >= 60 ? 'bg-brand-green-light' : pct >= 40 ? 'bg-brand-gold' : 'bg-brand-coral';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium text-brand-ink">{info.label}</span>
        <span className="text-brand-muted text-xs">
          {gracePeriodExcluded
            ? <span className="text-gray-300 italic">excluded (grace period)</span>
            : score !== null ? `${Math.round(score)} / 100` : '—'
          }
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        {!gracePeriodExcluded && score !== null && (
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
      <p className="text-xs text-gray-400">{info.desc} · weight {Math.round(weight * 100)}%</p>
    </div>
  );
}

function TrendSpark({ points }: { points: Array<{ date: string; overallScore: number }> }) {
  if (points.length < 2) return <p className="text-xs text-gray-400 mt-2">Not enough history yet.</p>;

  const max    = Math.max(...points.map((p) => p.overallScore), 1);
  const w      = 280;
  const h      = 48;
  const coords = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - (p.overallScore / max) * h;
    return `${x},${y}`;
  });
  const path = `M ${coords.join(' L ')}`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible">
      <path d={path} fill="none" stroke="#1D9E75" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Latest point dot */}
      {points.length > 0 && (
        <circle
          cx={(points.length - 1) / (points.length - 1) * w}
          cy={h - ((points[points.length - 1]!.overallScore) / max) * h}
          r="4" fill="#1D9E75"
        />
      )}
    </svg>
  );
}

// ── Main tab component ────────────────────────────────────────────────────────

export function AiTruthTab({ domainId, domain, onSetupWizard }: Props) {
  const [score,   setScore]   = useState<Awaited<ReturnType<typeof api.aiTruth.getScore>>   | null>(null);
  const [trend,       setTrend]       = useState<Awaited<ReturnType<typeof api.aiTruth.getScoreTrend>> | null>(null);
  const [fresh,       setFresh]       = useState<Awaited<ReturnType<typeof api.aiTruth.getFreshness>> | null>(null);
  const [vlength,     setVlength]     = useState<Awaited<ReturnType<typeof api.aiTruth.getVisitLength>> | null>(null);
  const [servingMode, setServingMode] = useState<Awaited<ReturnType<typeof api.aiTruth.getServingMode>> | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [resolving,   setResolving]   = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      api.aiTruth.getScore(domainId),
      api.aiTruth.getScoreTrend(domainId),
      api.aiTruth.getFreshness(domainId),
      api.aiTruth.getVisitLength(domainId),
      api.aiTruth.getServingMode(domainId),
    ])
      .then(([s, t, f, v, m]) => {
        if (cancelled) return;
        setScore(s);
        setTrend(t);
        setFresh(f);
        setVlength(v);
        setServingMode(m);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load AI Truth data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [domainId]);

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm animate-pulse">
            <div className="h-4 w-40 bg-gray-100 rounded mb-3" />
            <div className="h-8 w-24 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-sm text-red-600">
        {error}
      </div>
    );
  }

  // ── Disconnected state (C10.5) ────────────────────────────────────────────────

  if (servingMode?.mode === 'disconnected') {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-red-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-xl shrink-0">⚠️</div>
            <div className="flex-1">
              <p className="font-semibold text-brand-ink font-serif">AI Truth plugin disconnected</p>
              <p className="text-sm text-brand-muted mt-1">
                The WordPress plugin on <strong>{domain}</strong> is no longer responding.
                Your answers are not visible to AI platforms until you take an action below.
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <button
              data-testid="btn-reconnect-plugin"
              disabled={resolving}
              onClick={async () => {
                setResolving(true);
                try {
                  await api.aiTruth.reconnectPlugin(domainId);
                  // Reload the page to show updated state
                  window.location.reload();
                } catch {
                  setResolving(false);
                }
              }}
              className="bg-brand-green text-white text-sm font-medium px-4 py-3 rounded-xl hover:bg-brand-green-dark transition-colors disabled:opacity-50"
            >
              Reconnect plugin →
            </button>
            <button
              data-testid="btn-switch-to-saas"
              disabled={resolving}
              onClick={async () => {
                setResolving(true);
                try {
                  await api.aiTruth.switchToSaas(domainId);
                  window.location.reload();
                } catch {
                  setResolving(false);
                }
              }}
              className="bg-white border border-gray-200 text-brand-ink text-sm font-medium px-4 py-3 rounded-xl hover:border-brand-green hover:text-brand-green transition-colors disabled:opacity-50"
            >
              Switch to SaaS serving
            </button>
          </div>
          <p className="text-xs text-brand-muted mt-3">
            <strong>Reconnect plugin</strong> — reinstall or re-activate the FixMySEO AI Truth WordPress plugin.<br />
            <strong>Switch to SaaS</strong> — we'll provision a <code className="font-mono bg-gray-100 px-1 rounded">*.customers.fixmyseo.io</code> subdomain (no plugin required).
          </p>
        </div>
      </div>
    );
  }

  // ── No answers yet ──────────────────────────────────────────────────────────

  if (!score || score.inputs.totalApproved === 0) {
    return (
      <div className="space-y-4">
        <div className="bg-white border border-gray-100 rounded-xl p-8 shadow-sm text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto text-2xl">✨</div>
          <div>
            <p className="font-semibold text-brand-ink font-serif text-lg">No approved answers yet</p>
            <p className="text-sm text-brand-muted mt-1">
              Approve at least one AI Truth answer to see your AI Accuracy Score.
            </p>
          </div>
          {onSetupWizard && (
            <button
              onClick={onSetupWizard}
              className="bg-brand-green text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-brand-green-dark transition-colors"
            >
              Connect AI Truth →
            </button>
          )}
        </div>
      </div>
    );
  }

  const bandCfg = BAND_CONFIG[score.band ?? 'building']!;

  return (
    <div className="space-y-5">

      {/* ── Hero: Composite Score ─────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-6">
          {score.overallScore !== null ? (
            <ScoreRing score={score.overallScore} band={score.band ?? 'building'} />
          ) : (
            <div className="w-36 h-36 flex items-center justify-center text-gray-300 text-4xl">—</div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-semibold tracking-[0.15em] uppercase text-brand-muted">AI Accuracy Score</span>
              {score.band && (
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${bandCfg.bg} ${bandCfg.color} ${bandCfg.border}`}>
                  {bandCfg.label}
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold font-serif text-brand-ink">
              {score.overallScore !== null ? score.overallScore.toFixed(1) : '—'}
              <span className="text-base font-normal text-brand-muted ml-1">/ 100</span>
            </h2>
            <p className="text-sm text-brand-muted mt-1 max-w-sm">
              Composite of Coverage, Grounding, Serving, Reach, and Freshness — traceable to each component below.
            </p>
            {score.gracePeriod && (
              <div className="mt-2 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <span className="text-amber-600 text-xs">⏳</span>
                <span className="text-xs text-amber-700">
                  <strong>Grace period</strong> — Serving &amp; Reach excluded until 10+ queries
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Trend sparkline */}
        {trend && trend.trend.length > 1 && (
          <div className="mt-5 pt-4 border-t border-gray-50">
            <p className="text-xs text-brand-muted mb-2">30-day trend</p>
            <TrendSpark points={trend.trend.map((t) => ({ date: t.date, overallScore: t.overallScore }))} />
          </div>
        )}
      </div>

      {/* ── Component Breakdown ───────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-brand-ink font-serif mb-4">Component breakdown</h3>
        <div className="space-y-4">
          {(Object.entries(score.components) as Array<[string, { score: number; weight: number }]>).map(([key, comp]) => (
            <ComponentBar
              key={key}
              name={key}
              score={comp.score}
              weight={comp.weight}
              gracePeriodExcluded={score.gracePeriod && (key === 'serving' || key === 'reach')}
            />
          ))}
        </div>
      </div>

      {/* ── Stats row ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
          <p className="text-xs text-brand-muted uppercase tracking-wide mb-1">Approved answers</p>
          <p className="text-3xl font-bold font-serif text-brand-ink">{score.inputs.totalApproved}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
          <p className="text-xs text-brand-muted uppercase tracking-wide mb-1">Question anticipation</p>
          <p className="text-3xl font-bold font-serif text-brand-ink">{score.inputs.questionAnticipationCount}</p>
          <p className="text-xs text-gray-400 mt-0.5">widetail questions</p>
        </div>
      </div>

      {/* ── TruthSynq: Pending re-drafts ─────────────────────────────────── */}
      {fresh && (fresh.pendingRedraftCount > 0 || fresh.deletedSourceCount > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-amber-800 font-serif">Pending re-approvals</p>
              <p className="text-sm text-amber-700 mt-0.5">
                TruthSynq has detected content changes. Review and approve the re-drafts below.
              </p>
            </div>
            <div className="shrink-0 ml-4 flex gap-2">
              <span className="bg-amber-200 text-amber-900 text-sm font-medium px-3 py-1 rounded-full">
                {fresh.pendingRedraftCount} pending
              </span>
            </div>
          </div>
          {fresh.deletedSourceCount > 0 && (
            <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span className="text-red-600 text-sm">⚠</span>
              <span className="text-sm text-red-700">
                <strong>{fresh.deletedSourceCount}</strong> answer{fresh.deletedSourceCount === 1 ? '' : 's'} flagged high-priority — source content may have been removed.
              </span>
            </div>
          )}
          {fresh.avgDecayDays !== null && (
            <p className="text-xs text-amber-600 mt-2">
              Average time since last approval: <strong>{Math.round(fresh.avgDecayDays)} days</strong>
            </p>
          )}
        </div>
      )}

      {/* ── Visit Length ──────────────────────────────────────────────────── */}
      {vlength && vlength.engines.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-brand-ink font-serif">Visit Length by AI engine</h3>
              <p className="text-xs text-brand-muted mt-0.5">Avg seconds on-page for visitors referred by each AI platform (last {vlength.windowDays} days)</p>
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {vlength.engines.map((e) => (
              <div key={e.engine} className="py-3 flex items-center justify-between">
                <span className="text-sm font-medium text-brand-ink">{ENGINE_LABELS[e.engine] ?? e.engine}</span>
                <div className="flex items-center gap-3 text-right">
                  <span className="text-xs text-brand-muted">{e.sessions} sessions</span>
                  <span className="text-sm font-semibold text-brand-green">
                    {Math.round(e.avgEngagementSeconds)}s
                  </span>
                </div>
              </div>
            ))}
          </div>
          {vlength.enginesBelow10.length > 0 && (
            <p className="text-xs text-gray-400 mt-3">
              Below 10-session threshold (not shown): {vlength.enginesBelow10.map((e) => ENGINE_LABELS[e] ?? e).join(', ')}
            </p>
          )}
        </div>
      )}

      {/* ── Last sync ─────────────────────────────────────────────────────── */}
      {fresh?.lastSyncAt && (
        <p className="text-xs text-gray-400 text-center">
          TruthSynq last synced {new Date(fresh.lastSyncAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
        </p>
      )}
    </div>
  );
}
